import { useEffect, useState } from 'react';

import { Alert, Button, Tag } from '../ds';
import {
  api,
  relativeTime,
  type DeploymentFailureDetails,
  type DeploymentHistory,
  type DeploymentRun,
} from '../api';
import { c, cardStyle, mono } from '../tokens';
import { PromotionReviewDialog, RELEASE_TAG_PATTERN } from './PromotionReviewDialog';
import { PromotionBanner } from './PromotionBanner';

const ACTIVE_REFRESH_MS = 10_000;
const IDLE_REFRESH_MS = 30_000;
type ResultLimit = 10 | 20 | 50;

function runPresentation(run: DeploymentRun): {
  label: string;
  variant: 'default' | 'info' | 'warning' | 'success' | 'danger';
} {
  if (run.status === 'queued') return { label: 'Queued', variant: 'warning' };
  if (run.status === 'in_progress') return { label: 'Pipeline running', variant: 'info' };
  if (run.status !== 'completed') return { label: 'Status unavailable', variant: 'default' };

  switch (run.conclusion) {
    case 'success':
      return { label: 'Pipeline succeeded', variant: 'success' };
    case 'failure':
    case 'timed_out':
    case 'startup_failure':
      return { label: 'Pipeline failed', variant: 'danger' };
    case 'action_required':
      return { label: 'Action required', variant: 'warning' };
    case 'cancelled':
      return { label: 'Cancelled', variant: 'default' };
    case 'skipped':
    case 'neutral':
      return { label: 'Skipped', variant: 'default' };
    case 'stale':
      return { label: 'Stale', variant: 'default' };
    default:
      return { label: 'Completed', variant: 'default' };
  }
}

function exactTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function suggestedReleaseTag(runs: DeploymentRun[]): string {
  const versions = runs.flatMap(run => {
    if (run.environment !== 'prod') return [];
    const match = /^v(\d+)\.(\d+)\.(\d+)$/.exec(run.branchOrTag);
    return match ? [{ major: Number(match[1]), minor: Number(match[2]), patch: Number(match[3]) }] : [];
  }).sort((left, right) =>
    right.major - left.major || right.minor - left.minor || right.patch - left.patch,
  );
  const latest = versions[0];
  return latest ? `v${latest.major}.${latest.minor}.${latest.patch + 1}` : 'v1.0.0';
}

export function DeploymentsPanel({ repo, canManage, devOnly = false, prodReady = true, active = true, previewOnly = false, view = 'history', onViewDeployments = () => undefined }: { repo: string; canManage: boolean; devOnly?: boolean; prodReady?: boolean; active?: boolean; previewOnly?: boolean; view?: 'history' | 'overview'; onViewDeployments?: () => void }) {
  const [history, setHistory] = useState<DeploymentHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [expandedFailureRunId, setExpandedFailureRunId] = useState<number | null>(null);
  const [failureDetails, setFailureDetails] = useState<Record<number, DeploymentFailureDetails>>({});
  const [failureLoadingRunId, setFailureLoadingRunId] = useState<number | null>(null);
  const [failureErrors, setFailureErrors] = useState<Record<number, string>>({});
  const [promotionModalOpen, setPromotionModalOpen] = useState(false);
  const [promotionTag, setPromotionTag] = useState('v1.0.0');
  const [promotionConfirmed, setPromotionConfirmed] = useState(false);
  const [promotionSaving, setPromotionSaving] = useState(false);
  const [promotionError, setPromotionError] = useState<string | null>(null);
  const [promotionSuccess, setPromotionSuccess] = useState<string | null>(null);
  const [resultLimit, setResultLimit] = useState<ResultLimit>(10);

  const toggleFailureOutput = (runId: number): void => {
    if (expandedFailureRunId === runId) {
      setExpandedFailureRunId(null);
      return;
    }

    setExpandedFailureRunId(runId);
    setFailureErrors(current => {
      const next = { ...current };
      delete next[runId];
      return next;
    });
    if (failureDetails[runId]) return;

    setFailureLoadingRunId(runId);
    api.deploymentFailure(repo, runId)
      .then(details => setFailureDetails(current => ({ ...current, [runId]: details })))
      .catch((err: unknown) => {
        setFailureErrors(current => ({
          ...current,
          [runId]: err instanceof Error ? err.message : 'Could not load failed-step output.',
        }));
      })
      .finally(() => setFailureLoadingRunId(current => current === runId ? null : current));
  };

  useEffect(() => {
    let cancelled = false;
    let refreshTimer: number | undefined;

    if (!active) return;
    const onVisibility = () => { if (!document.hidden) setRefreshNonce(current => current + 1); };
    document.addEventListener('visibilitychange', onVisibility);
    if (document.hidden) return () => document.removeEventListener('visibilitychange', onVisibility);
    if (history) setRefreshing(true);
    else setLoading(true);

    api.deployments(repo)
      .then(nextHistory => {
        if (cancelled) return;
        setHistory(nextHistory);
        setError(null);

        const hasActiveRun = nextHistory.runs.some(run =>
          run.status === 'queued' || run.status === 'in_progress',
        );
        refreshTimer = window.setTimeout(
          () => setRefreshNonce(current => current + 1),
          hasActiveRun ? ACTIVE_REFRESH_MS : IDLE_REFRESH_MS,
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setHistory(null);
        setError(err instanceof Error ? err.message : 'Could not load deployment history.');
        refreshTimer = window.setTimeout(() => setRefreshNonce(current => current + 1), IDLE_REFRESH_MS);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
    };
  }, [repo, refreshNonce, active]);

  const latestDevAttempt = history?.runs.find(run => run.environment === 'dev');
  const latestDevFailed = latestDevAttempt?.status === 'completed'
    && ['failure', 'timed_out', 'startup_failure'].includes(latestDevAttempt.conclusion ?? '');
  const latestDev = history?.runs.find(run =>
    run.environment === 'dev' && run.status === 'completed' && run.conclusion === 'success',
  );
  const latestDevPromotionStarted = latestDev ? history?.runs.some(run =>
    run.environment === 'prod' && run.commitSha === latestDev.commitSha &&
    (run.status === 'queued' || run.status === 'in_progress' || run.conclusion === 'success'),
  ) : false;
  const canStartPromotion = Boolean(latestDev && !latestDevPromotionStarted);
  const promotionTagValid = RELEASE_TAG_PATTERN.test(promotionTag);
  const visibleRuns = history?.runs.slice(0, resultLimit);

  const openPromotionModal = (): void => {
    if (!history) return;
    setPromotionConfirmed(false);
    setPromotionTag(suggestedReleaseTag(history.runs));
    setPromotionError(null);
    setPromotionModalOpen(true);
  };

  const startPromotion = (): void => {
    if (!promotionTagValid || !promotionConfirmed || promotionSaving || !canManage || devOnly || !prodReady || !canStartPromotion) return;
    setPromotionSaving(true);
    setPromotionError(null);
    api.promoteToProduction(repo, { tag: promotionTag })
      .then(({ promotion }) => {
        setPromotionSuccess(
          `${promotion.tag} was created for ${promotion.commitSha.slice(0, 7)}. Production promotion is starting.`,
        );
        setPromotionModalOpen(false);
        setRefreshNonce(current => current + 1);
      })
      .catch((err: unknown) => {
        setPromotionError(err instanceof Error ? err.message : 'Could not start production promotion.');
      })
      .finally(() => setPromotionSaving(false));
  };

  return (
    <>
    {history?.cache?.stale && <p role="status">Last checked {new Date(history.cache.checkedAt).toLocaleTimeString()} · May be out of date</p>}
    {view === 'history' ? <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="deployment-panel-heading">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}>
            Deployment pipelines
          </h2>
          <p style={{ margin: 0, color: c.textBody, fontSize: 13.5, lineHeight: 1.5 }}>
            Track application deployments across environments.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {canManage && !devOnly && (
            <Button
              variant="primary"
              size="sm"
              disabled={loading || !canStartPromotion || !prodReady}
              onPress={openPromotionModal}
            >
              Promote to Prod
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            loading={refreshing}
            disabled={loading || refreshing}
            onPress={() => setRefreshNonce(current => current + 1)}
          >
            Refresh
          </Button>
        </div>
      </div>

      {!devOnly && latestDevFailed && <Alert variant="warning" showIcon>
        Latest Dev deployment ({latestDevAttempt?.commitSha.slice(0, 7)}) failed.
        {latestDev ? ` Promotion uses the last successful Dev version (${latestDev.commitSha.slice(0, 7)}), not the failed changes.` : ' No successful Dev version is available to promote.'}
      </Alert>}
      {promotionSuccess && (
        <Alert variant="success" showIcon>{promotionSuccess}</Alert>
      )}

      {canManage && !devOnly && !loading && !latestDev && (
        <Alert variant="warning" showIcon>
          A successful Dev deployment is required before this app can be promoted to Prod.
        </Alert>
      )}

      {canManage && !devOnly && latestDevPromotionStarted && (
        <Alert variant="info" showIcon>
          The latest successful Dev deployment is already promoted or being promoted to Prod.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" showIcon>
          {error}
        </Alert>
      )}

      {loading ? (
        <div style={{ ...cardStyle, boxShadow: 'none', padding: 24, color: c.textMuted, fontSize: 14 }}>
          Loading deployment history...
        </div>
      ) : history?.runs.length ? (
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          {visibleRuns?.map((run, index) => {
            const presentation = runPresentation(run);
            const timestamp = run.completedAt ?? run.startedAt ?? run.createdAt;
            const canShowFailure = run.conclusion === 'failure' ||
              run.conclusion === 'timed_out' || run.conclusion === 'startup_failure';
            const failureExpanded = expandedFailureRunId === run.id;
            const details = failureDetails[run.id];
            return (
              <article
                key={run.id}
                className="deployment-run-row"
                style={{ borderTop: index === 0 ? 0 : `1px solid ${c.border}` }}
              >
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 475 }}>{run.workflowName}</span>
                    <Tag variant={run.environment === 'prod' ? 'warning' : 'default'} size="sm">
                      {run.environment === 'prod' ? 'Prod' : 'Dev'}
                    </Tag>
                    <Tag variant={presentation.variant} size="sm">{presentation.label}</Tag>
                  </div>
                  <span
                    title={run.commitMessage}
                    style={{ color: c.textBody, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {run.commitMessage || 'No commit message'}
                  </span>
                  <div style={{ display: 'flex', gap: '6px 14px', flexWrap: 'wrap', color: c.textMuted, fontSize: 12.5 }}>
                    <span style={{ fontFamily: mono }}>{run.commitSha.slice(0, 7)}</span>
                    <span>{run.branchOrTag || 'Unknown ref'}</span>
                    <span>Run #{run.runNumber}{run.attempt > 1 ? `, attempt ${run.attempt}` : ''}</span>
                    <span>by {run.actor}</span>
                  </div>
                </div>
                <div className="deployment-run-actions">
                  <time dateTime={timestamp} title={exactTime(timestamp)} style={{ color: c.textMuted, fontSize: 12.5 }}>
                    {relativeTime(timestamp)}
                  </time>
                  {canShowFailure && (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={failureLoadingRunId === run.id}
                      disabled={failureLoadingRunId === run.id}
                      onPress={() => toggleFailureOutput(run.id)}
                    >
                      {failureExpanded ? 'Hide failure output' : 'Show failure output'}
                    </Button>
                  )}
                </div>
                {failureExpanded && (
                  <div className="deployment-failure-output">
                    {failureErrors[run.id] ? (
                      <Alert variant="danger" showIcon>{failureErrors[run.id]}</Alert>
                    ) : details ? (
                      details.steps.length ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                          {details.steps.map((step, stepIndex) => (
                            <div key={`${step.jobName}-${step.stepName}-${stepIndex}`} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                <strong style={{ fontSize: 13.5, fontWeight: 525 }}>{step.stepName}</strong>
                                <span style={{ color: c.textMuted, fontSize: 12.5 }}>{step.jobName}</span>
                              </div>
                              <pre className="deployment-failure-log">{step.output}</pre>
                            </div>
                          ))}
                          <span style={{ color: c.textMuted, fontSize: 12 }}>
                            Output is automatically redacted and limited to the failed step.
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: c.textMuted, fontSize: 13.5 }}>
                          GitHub did not identify a failed step with available output for this run.
                        </span>
                      )
                    ) : (
                      <span style={{ color: c.textMuted, fontSize: 13.5 }}>Loading failed-step output...</span>
                    )}
                  </div>
                )}
              </article>
            );
          })}
          <div
            style={{
              borderTop: `1px solid ${c.border}`,
              padding: '12px 22px',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: c.textMuted, fontSize: 12.5 }}>
              Show last
              <select
                aria-label="Number of recent deployment results"
                value={resultLimit}
                onChange={event => setResultLimit(Number(event.target.value) as ResultLimit)}
                style={{
                  border: `1px solid ${c.border}`,
                  borderRadius: 8,
                  padding: '6px 28px 6px 10px',
                  background: c.surface,
                  color: c.textBody,
                  font: 'inherit',
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              results
            </label>
          </div>
        </div>
      ) : !error ? (
        <div
          style={{
            border: `1px dashed ${c.border}`,
            borderRadius: 16,
            padding: 32,
            background: c.subtle,
            color: c.textFaint,
            fontSize: 13.5,
          }}
        >
          No Dev or Prod workflow runs have been recorded for this app yet.
        </div>
      ) : null}
    </section> : <PromotionBanner dev={latestDev} devAttempt={latestDevAttempt}
      prod={history?.runs.find(run => run.environment === 'prod' && run.status === 'completed' && run.conclusion === 'success')}
      promotionRun={latestDev ? history?.runs.find(run => run.environment === 'prod' && run.commitSha === latestDev.commitSha && (run.status === 'queued' || run.status === 'in_progress' || run.conclusion === 'success')) : undefined}
      loading={loading} error={error} canManage={canManage && !devOnly} prodReady={prodReady}
      onPromote={openPromotionModal} onViewDeployments={onViewDeployments} onRefresh={() => setRefreshNonce(current => current + 1)} />}


    {promotionModalOpen && latestDev && (
      <PromotionReviewDialog commitSha={latestDev.commitSha} commitMessage={latestDev.commitMessage}
        tag={promotionTag} onTagChange={setPromotionTag}
        confirmed={promotionConfirmed} onConfirmedChange={setPromotionConfirmed}
        saving={promotionSaving} error={promotionError} warning={latestDevFailed ? `The latest Dev attempt (${latestDevAttempt?.commitSha.slice(0, 7)}) failed. You’re promoting the last successful Dev deployment (${latestDev.commitSha.slice(0, 7)}), not the failed changes.` : undefined} previewOnly={previewOnly}
        onClose={() => setPromotionModalOpen(false)} onPromote={startPromotion} />
    )}
    </>
  );
}
